// ===== O cadastro do CRM vira cliente no Nomus =====
//
// Contrato: uploads/nomus_api_clientes.md (POST /pdled/rest/clientes)
//
// POR QUE ISTO EXISTE: até 18/09 `syncClienteERP` INVENTAVA o identificador --
// `String(40000 + Math.random() * 59999)` -- e escrevia um log de API falso
// dizendo "Cliente criado no Nomus ERP · ID 47231". Nenhuma chamada acontecia.
//
// Era o segundo caso mais grave da varredura de mocks, e tinha consequência
// medida: `id_pessoa_nomus` nunca era preenchido de verdade, e o primeiro envio
// real de pedido barrou em "Cliente não tem idPessoaCliente".
//
// A ARMADILHA DESTE CONTRATO são as MÁSCARAS. O Nomus espera
// `"00.836.589/0001-62"` e `"138.572.927-92"`; o nosso banco guarda só dígitos
// (`clientes.cnpj` tem comentário explícito: "só dígitos"). Mandar sem máscara
// não é erro de sintaxe -- é um cadastro criado com o documento errado, que só
// aparece na hora de emitir a nota.

const digitos = (v) => String(v ?? '').replace(/\D/g, '');

/** `12345678000190` -> `12.345.678/0001-90`. Sem 14 dígitos, devolve vazio. */
export function mascaraCnpj(v) {
  const d = digitos(v);
  if (d.length !== 14) return '';
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** `13857292792` -> `138.572.927-92`. Sem 11 dígitos, devolve vazio. */
export function mascaraCpf(v) {
  const d = digitos(v);
  if (d.length !== 11) return '';
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/** `20040005` -> `20040-005`. CEP incompleto não vira máscara torta. */
export function mascaraCep(v) {
  const d = digitos(v);
  if (d.length !== 8) return '';
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/**
 * Telefone no formato que o Nomus documenta: `"+55 21 99999-0000"`.
 *
 * Aceita o que o vendedor digitar. Sem DDD reconhecível devolve o que veio --
 * telefone mal formatado ainda é melhor que telefone apagado, e o campo é
 * opcional no contrato.
 */
export function telefoneComDdi(v) {
  const bruto = String(v ?? '').trim();
  if (!bruto) return '';
  let d = digitos(bruto);
  if (d.startsWith('55') && d.length >= 12) d = d.slice(2);
  if (d.length < 10 || d.length > 11) return bruto;
  const ddd = d.slice(0, 2);
  const resto = d.slice(2);
  const meio = resto.length === 9 ? resto.slice(0, 5) : resto.slice(0, 4);
  return `+55 ${ddd} ${meio}-${resto.slice(meio.length)}`;
}

/**
 * Regime tributário. O valor GRAVADO no cadastro (1, 2 ou 3) manda; só quando
 * ele falta vale a inferência pela regra do contrato:
 *   CNPJ com IE -> 1 (contribuinte) · CNPJ sem IE -> 3 · PF -> 3
 *
 * POR QUE O GRAVADO MANDA (decisão do Kaio, 30/09: "importantes para o cadastro
 * do cliente no ERP e para o cálculo de ICMS-ST"). Medido: 1.735 dos 2.201
 * clientes têm o tipo gravado (veio do Nomus), e a inferência diverge dele em
 * 159: os 151 "contribuinte isento" (tipo 2, que ela NUNCA produz), 3 que
 * perderiam o ST e 5 que o ganhariam sem direito. Antes disto o payload mandava
 * sempre a inferência e ignorava o que estava gravado.
 *
 * O `3` de não contribuinte é o mesmo valor que faz o ICMS-ST NÃO incidir
 * (src/icms-st.js): errar aqui muda o imposto da venda, não só o cadastro.
 */
export function tipoContribuinte(cliente) {
  if (!cliente) return 3;
  // O <select> do formulário entrega o valor como texto ("2").
  const gravado = Number(cliente.tipoContribuinte);
  if (cliente.tipoContribuinte !== '' && cliente.tipoContribuinte != null && [1, 2, 3].includes(gravado)) return gravado;
  if (cliente.tipoDoc === 'cpf') return 3;
  const ie = String(cliente.ie ?? '').trim().toLowerCase();
  return ie && ie !== 'isento' ? 1 : 3;
}

/**
 * O que impede o envio, em português.
 *
 * Confere ANTES de chamar: o Nomus devolve erro genérico, e o vendedor leria
 * "não foi possível criar" sem saber que faltou o documento.
 */
export function conferirParaErp(cliente) {
  const erros = [];
  if (!String(cliente?.nome ?? '').trim()) erros.push('Nome é obrigatório.');
  if (cliente?.tipoDoc === 'cpf') {
    if (digitos(cliente?.cpf).length !== 11) erros.push('CPF inválido ou ausente.');
  } else if (digitos(cliente?.cnpj).length !== 14) {
    erros.push('CNPJ inválido ou ausente.');
  }
  return erros;
}

/** O corpo do POST /clientes. */
export function payloadDoCliente(cliente) {
  if (!cliente) return {};
  const end = cliente.endereco || {};
  const pj = cliente.tipoDoc !== 'cpf';
  const ie = String(cliente.ie ?? '').trim();

  return {
    nome: cliente.nome || '',
    // Para PF o contrato manda repetir o nome na razão social.
    razaoSocial: cliente.razaoSocial || cliente.nome || '',
    tipoPessoa: pj ? 1 : 2,
    tipoContribuinteICMS: tipoContribuinte(cliente),
    ...(pj ? { cnpj: mascaraCnpj(cliente.cnpj) } : { cpf: mascaraCpf(cliente.cpf) }),
    // "isento" é rótulo da nossa tela, não inscrição. Mandá-lo criaria um
    // cadastro cuja IE é a palavra isento.
    inscricaoEstadual: pj && ie && ie.toLowerCase() !== 'isento' ? ie : '',
    ativo: true,
    email: cliente.email || '',
    telefone: telefoneComDdi(cliente.telefone),
    endereco: end.rua || '',
    numero: end.num || '',
    complemento: end.complemento || '',
    bairro: end.bairro || '',
    municipio: end.cidade || cliente.cidade || '',
    uf: (end.uf || '').slice(0, 2).toUpperCase(),
    cep: mascaraCep(end.cep),
    pais: 'BRASIL',
    suspenderCobranca: false,
  };
}
